// Tills and devices: who is set up, and (slice 51b, ADR 0047) setting one up,
// replacing a lost one, recovering a restored one, and revoking a stolen one.
//
// The enrollment code a till needs is shown ONCE, here, in memory only: the
// server stores a hash, a repeated request returns no code, and this page
// never writes it to storage or the URL. Every action that ends or replaces a
// till is a two-step: say what will happen, then confirm.

import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../api/client.js';
import type { DeviceWithCode, PosConnection, PosDevice } from '../../api/pos-types.js';
import { Chip, QueryState, actionErrorText, type TabProps } from './common.js';
import { formatDateTime, humanize } from './format.js';

const TONE: Record<string, 'ok' | 'warn' | 'bad' | undefined> = {
  active: 'ok',
  pending_enrollment: 'warn',
  suspended: 'warn',
  revoked: 'bad',
  retired: undefined,
};

const ROLE_TEXT = {
  issuer: 'Billing till (issues invoices; one per outlet)',
  waiter: 'Waiter tablet',
  kitchen: 'Kitchen screen',
  hub: 'Outlet hub (one per outlet)',
} as const;
type Role = keyof typeof ROLE_TEXT;

export interface DevicesTabProps extends TabProps {
  readonly restaurantId: string | undefined;
  /** pos:device_manage */
  readonly canManage: boolean;
  /** integration:manage */
  readonly canEnable: boolean;
}

type Pending = { readonly kind: 'revoke' | 'recover' | 'replace'; readonly device: PosDevice };

interface ShownCode {
  readonly deviceName: string;
  readonly code: string;
  readonly minutes: number | undefined;
}

const CONFIRM_TEXT: Record<Pending['kind'], string> = {
  revoke:
    'Revoking ends this till for good: it stops signing in at once and cannot be brought back. Its invoice numbers are closed.',
  recover:
    'Use this when the till was restored from a backup or copied. It starts a new generation and needs setting up again with a new code; the old credential stops working.',
  replace:
    'Use this when the till is lost, broken or stolen. The old one is ended, its invoice numbers are closed, and a new till takes its place with a new code.',
};

export function DevicesTab({
  orgId,
  outletId,
  restaurantId,
  canManage,
  canEnable,
}: DevicesTabProps): JSX.Element {
  const qc = useQueryClient();
  const base = `/organizations/${orgId}/outlets/${outletId}/pos`;
  const devicesKey = ['pos-devices', orgId, outletId] as const;
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('issuer');
  const [pending, setPending] = useState<Pending | null>(null);
  const [replacementName, setReplacementName] = useState('');
  const [shown, setShown] = useState<ShownCode | null>(null);

  const devices = useQuery({
    queryKey: devicesKey,
    queryFn: () => apiFetch<{ devices: PosDevice[] }>(`${base}/devices`),
  });
  const connectionKey = ['pos-connection', orgId, restaurantId] as const;
  const connection = useQuery({
    queryKey: connectionKey,
    enabled: Boolean(restaurantId),
    queryFn: () =>
      apiFetch<{ connection: PosConnection | null }>(
        `/organizations/${orgId}/restaurants/${restaurantId}/pos/connection`,
      ),
  });
  const enabled = connection.data?.connection?.status === 'active';

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: devicesKey });
    void qc.invalidateQueries({ queryKey: ['pos-series', orgId, outletId] });
  };
  const reveal = (res: DeviceWithCode): void => {
    if (res.enrollmentCode) {
      setShown({
        deviceName: res.device.name,
        code: res.enrollmentCode,
        minutes: res.enrollmentCodeExpiresInMinutes,
      });
    }
  };

  const enable = useMutation({
    mutationFn: () =>
      apiFetch<PosConnection>(
        `/organizations/${orgId}/restaurants/${restaurantId}/pos/connection`,
        {
          method: 'PUT',
          body: {},
        },
      ),
    onSettled: () => void qc.invalidateQueries({ queryKey: connectionKey }),
  });
  const create = useMutation({
    mutationFn: () =>
      apiFetch<DeviceWithCode>(`${base}/devices`, {
        method: 'POST',
        body: { name: name.trim(), role },
      }),
    onSuccess: (res) => {
      setName('');
      reveal(res);
    },
    onSettled: refresh,
  });
  const act = useMutation({
    mutationFn: (p: Pending) =>
      apiFetch<DeviceWithCode | { device: PosDevice }>(`${base}/devices/${p.device.id}/${p.kind}`, {
        method: 'POST',
        body:
          p.kind === 'replace' && replacementName.trim() ? { name: replacementName.trim() } : {},
      }),
    onSuccess: (res) => {
      setPending(null);
      if ('enrollmentCode' in res) reveal(res as DeviceWithCode);
    },
    onSettled: refresh,
  });
  const error = actionErrorText(enable.error ?? create.error ?? act.error);
  const busy = enable.isPending || create.isPending || act.isPending;

  return (
    <section aria-labelledby="devices-heading">
      <h2 id="devices-heading">Tills and devices</h2>
      <QueryState loading={devices.isLoading} error={devices.error} />
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}

      {shown ? (
        <div className="card code-card" role="status" data-testid="enrollment-code">
          <p>
            <strong>Type this code into “{shown.deviceName}”.</strong>
          </p>
          <p className="enrollment-code">{shown.code}</p>
          <p className="muted">
            It is shown only now and cannot be looked up again
            {shown.minutes ? `; it stops working after ${shown.minutes} minutes` : ''}. If it is
            lost, set the till up again.
          </p>
          <button type="button" onClick={() => setShown(null)}>
            I have entered it
          </button>
        </div>
      ) : null}

      {devices.data && devices.data.devices.length === 0 ? (
        <p className="muted">No devices have been set up for this outlet.</p>
      ) : null}
      {devices.data && devices.data.devices.length > 0 ? (
        <div className="table-wrap">
          <table className="pos-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Role</th>
                <th scope="col">Status</th>
                <th scope="col">Enrolled</th>
                <th scope="col">Last seen</th>
                <th scope="col">Ended</th>
                {canManage ? <th scope="col">Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {devices.data.devices.map((d) => {
                const live = ['pending_enrollment', 'active', 'suspended'].includes(d.status);
                const running = ['active', 'suspended'].includes(d.status);
                return (
                  <tr key={d.id}>
                    <th scope="row">{d.name}</th>
                    <td>{d.role}</td>
                    <td>
                      <Chip tone={TONE[d.status]}>{humanize(d.status)}</Chip>
                    </td>
                    <td>{formatDateTime(d.enrolledAt)}</td>
                    <td>{formatDateTime(d.lastSeenAt)}</td>
                    <td>{formatDateTime(d.endedAt)}</td>
                    {canManage ? (
                      <td>
                        <div className="row-actions">
                          {live ? (
                            <button
                              type="button"
                              className="secondary"
                              disabled={busy}
                              onClick={() => {
                                setReplacementName(d.name);
                                setPending({ kind: 'replace', device: d });
                              }}
                            >
                              Replace
                            </button>
                          ) : null}
                          {running ? (
                            <button
                              type="button"
                              className="secondary"
                              disabled={busy}
                              onClick={() => setPending({ kind: 'recover', device: d })}
                            >
                              Recover
                            </button>
                          ) : null}
                          {live ? (
                            <button
                              type="button"
                              className="secondary"
                              disabled={busy}
                              onClick={() => setPending({ kind: 'revoke', device: d })}
                            >
                              Revoke
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {pending ? (
        <div
          className="card"
          role="alertdialog"
          aria-label={`${pending.kind} ${pending.device.name}`}
        >
          <p>
            <strong>
              {humanize(pending.kind)} “{pending.device.name}”?
            </strong>
          </p>
          <p>{CONFIRM_TEXT[pending.kind]}</p>
          {pending.kind === 'replace' ? (
            <label className="inline-field">
              Name of the new till{' '}
              <input
                type="text"
                maxLength={80}
                value={replacementName}
                onChange={(e) => setReplacementName(e.target.value)}
              />
            </label>
          ) : null}
          <div className="row-actions">
            <button type="button" disabled={busy} onClick={() => act.mutate(pending)}>
              Yes, {pending.kind}
            </button>
            <button type="button" className="secondary" onClick={() => setPending(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {canManage ? (
        <section className="card" aria-labelledby="setup-heading">
          <h3 id="setup-heading">Set up a till</h3>
          {restaurantId && connection.isSuccess && !enabled ? (
            <>
              <p>Tills are not turned on for this restaurant yet.</p>
              {canEnable ? (
                <button type="button" disabled={busy} onClick={() => enable.mutate()}>
                  Turn on tills for this restaurant
                </button>
              ) : (
                <p className="muted">An owner needs to turn them on first.</p>
              )}
            </>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (name.trim()) create.mutate();
              }}
            >
              <label className="inline-field">
                Name{' '}
                <input
                  type="text"
                  maxLength={80}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Counter 1"
                />
              </label>{' '}
              <label className="inline-field">
                Kind{' '}
                <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
                  {(Object.keys(ROLE_TEXT) as Role[]).map((r) => (
                    <option key={r} value={r}>
                      {ROLE_TEXT[r]}
                    </option>
                  ))}
                </select>
              </label>{' '}
              <button type="submit" disabled={busy || name.trim() === ''}>
                Create and show the code
              </button>
              <p className="muted">
                You get a one-time code to type into the till. It is shown once.
              </p>
            </form>
          )}
        </section>
      ) : (
        <p className="muted">Setting up, replacing or revoking a till needs an owner or manager.</p>
      )}
    </section>
  );
}
